import { Color, Spacing } from '@persona-id/design-tokens';
import { useState } from 'react';
import { Control, UseFormSetValue, UseFormWatch } from 'react-hook-form';
import styled from 'styled-components';

import { GraphNodeConfigFormData } from 'dashboard/forms/FormGraphNodeConfiguration/FormGraphNodeConfiguration';
import NodeFunctionConfiguration from 'dashboard/forms/FormGraphNodeConfiguration/NodeFunctionsConfigurationTab/NodeFunctionConfiguration';
import NodeFunctionsNavigator from 'dashboard/forms/FormGraphNodeConfiguration/NodeFunctionsConfigurationTab/NodeFunctionsNavigator';
import { AccountType } from 'dashboard/models/account-type';
import { PresetType, ServerPresetSummary } from 'dashboard/models/graph-backend';
import Card from 'design-system/components/Card';

interface NodeFunctionsConfigurationTabProps {
  accountTypes: AccountType[];
  watch: UseFormWatch<GraphNodeConfigFormData>;
  control: Control<GraphNodeConfigFormData, object>;
  setValue: UseFormSetValue<GraphNodeConfigFormData>;
  presetSummaries: ServerPresetSummary[];
  disabled: boolean;
  name: string;
}

export const PRESET_TYPE_TO_LABEL: Record<PresetType, string> = {
  [PresetType.NormalizeDecimal]: 'Standardize decimal value',
  [PresetType.NormalizeAddress]: 'Standardize address format',
  [PresetType.NormalizeDate]: 'Standardize date format',
  [PresetType.NormalizeDateTime]: 'Standardize date and time format (ISO 8601)',
  [PresetType.NormalizeEmail]: 'Standardize email address',
  [PresetType.NormalizeGovernmentId]: 'Standardize government ID',
  [PresetType.NormalizeName]: 'Standardize name',
  [PresetType.NormalizeIdDocumentNumber]: 'Normalize various document values',
  [PresetType.NormalizeIpAddress]: 'Standardize IP address',
  [PresetType.NormalizePhoneNumber]: 'Standardize phone number',
  [PresetType.NormalizePostalCode]: 'Standardize postal code (global)',
  [PresetType.NormalizeSocialSecurityNumber]: 'Standardize social security number',
  [PresetType.NormalizeSimilarityTargets]: 'Standardize similarity targets',
  [PresetType.ValidateAccount]: 'Validate account token',
  [PresetType.ValidateBoolean]: 'Validate boolean',
  [PresetType.ValidateInteger]: 'Validate integer',
  [PresetType.ValidateSocialSecurityNumberLast4]: 'Validate social security last four',
  [PresetType.ValidateString]: 'Validate string',
  [PresetType.ValidateUuid]: 'Validate UUID',
  [PresetType.ValidateBooleanFalse]: 'Validate boolean is false',
  [PresetType.RunRegex]: 'Run regular expression',
};

const Container = styled.div`
  display: flex;
`;

const PresetContainer = styled.div`
  flex: 1;
  border-right: 1px solid ${Color.CoreSlate100};
`;

const NodeFunctionContainer = styled.div`
  padding: ${Spacing[5]};
  flex: 3;
`;

const NodeFunctionsConfigurationTab = ({
  watch,
  control,
  setValue,
  presetSummaries,
  accountTypes,
  disabled,
  name,
}: NodeFunctionsConfigurationTabProps) => {
  const nodeFunctions = watch('nodeFunctionsConfig.functions');
  // NOTE(unzi): Currently all datum types have 1 node function configured
  const [selectedFunctionToken, setSelectedFunctionToken] = useState<string | undefined>(
    nodeFunctions?.[0].token,
  );
  const nodeFunction =
    nodeFunctions.find((nf) => nf.token === selectedFunctionToken) || nodeFunctions?.[0];
  const selectedPreset = presetSummaries?.find((ps) => ps.preset === nodeFunction?.preset);

  const [autoMapperTimeout, setAutoMapperTimeout] = useState<NodeJS.Timeout | undefined>(undefined);

  const cancelAutoMapper = () => {
    if (autoMapperTimeout) {
      clearTimeout(autoMapperTimeout);
      setAutoMapperTimeout(undefined);
    }
  };

  return (
    <Card>
      <Container>
        <PresetContainer>
          <NodeFunctionsNavigator
            control={control}
            setValue={setValue}
            setSelectedFunctionToken={setSelectedFunctionToken}
            selectedFunctionToken={nodeFunction.token}
            nodeFunctions={nodeFunctions}
            accountTypes={accountTypes}
            presetSummaries={presetSummaries}
            disabled={disabled}
            cancelAutoMapper={cancelAutoMapper}
          />
        </PresetContainer>
        <NodeFunctionContainer>
          {selectedPreset && nodeFunction && (
            <NodeFunctionConfiguration
              watch={watch}
              setValue={setValue}
              nodeFunction={nodeFunction}
              presetSummary={selectedPreset}
              presetSummaries={presetSummaries}
              accountTypes={accountTypes}
              disabled={disabled}
              name={name}
              autoMapperTimeout={autoMapperTimeout}
              setAutoMapperTimeout={setAutoMapperTimeout}
            />
          )}
        </NodeFunctionContainer>
      </Container>
    </Card>
  );
};

export default NodeFunctionsConfigurationTab;
